import { useState, useEffect } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  // Handle scroll effect for header
  if (typeof window !== 'undefined') {
    window.addEventListener('scroll', () => {
      setIsScrolled(window.scrollY > 50);
    });
  }

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Calculator logic
  useEffect(() => {
    const FX_RATE = 155;
    const principalInput = document.getElementById('cs_principal_jpy') as HTMLInputElement;
    const planSelect = document.getElementById('cs_plan') as HTMLSelectElement;
    const fxLabel = document.getElementById('cs_fx_label');
    const principalUsdtHint = document.getElementById('cs_principal_usdt_hint');
    const daysDisplay = document.getElementById('cs_days');
    const rateDisplay = document.getElementById('cs_rate');
    const dailyJpy = document.getElementById('cs_daily_jpy');
    const dailyUsdt = document.getElementById('cs_daily_usdt');
    const monthlyJpy = document.getElementById('cs_monthly_jpy');
    const monthlyUsdt = document.getElementById('cs_monthly_usdt');
    const totalJpy = document.getElementById('cs_total_jpy');
    const totalUsdt = document.getElementById('cs_total_usdt');

    if (fxLabel) fxLabel.textContent = String(FX_RATE);

    const planData: Record<string, { days: number; rate: number }> = {
      lock360: { days: 360, rate: 0.005 },
      lock180: { days: 180, rate: 0.004 },
      lock90: { days: 90, rate: 0.0035 },
      lock30: { days: 30, rate: 0.003 },
      aidaily: { days: 365, rate: 0.00175 }
    };

    const calculate = () => {
      const principalJpy = parseFloat(principalInput?.value || '0');
      const planKey = planSelect?.value || 'lock360';
      const { days, rate } = planData[planKey];

      if (principalUsdtHint) {
        const usdt = principalJpy / FX_RATE;
        principalUsdtHint.textContent = `参考:${usdt.toFixed(2)} USDT`;
      }

      if (daysDisplay) daysDisplay.textContent = `${days}日`;
      if (rateDisplay) rateDisplay.textContent = `${(rate * 100).toFixed(3)}%`;

      if (principalJpy > 0) {
        const dailyProfitJpy = principalJpy * rate;
        const dailyProfitUsdt = dailyProfitJpy / FX_RATE;
        const monthlyProfitJpy = dailyProfitJpy * 30;
        const monthlyProfitUsdt = monthlyProfitJpy / FX_RATE;
        const totalProfitJpy = dailyProfitJpy * days;
        const totalProfitUsdt = totalProfitJpy / FX_RATE;

        if (dailyJpy) dailyJpy.textContent = `${dailyProfitJpy.toLocaleString('ja-JP')} 円`;
        if (dailyUsdt) dailyUsdt.textContent = `参考:${dailyProfitUsdt.toFixed(2)} USDT`;
        if (monthlyJpy) monthlyJpy.textContent = `${monthlyProfitJpy.toLocaleString('ja-JP')} 円`;
        if (monthlyUsdt) monthlyUsdt.textContent = `参考:${monthlyProfitUsdt.toFixed(2)} USDT`;
        if (totalJpy) totalJpy.textContent = `${totalProfitJpy.toLocaleString('ja-JP')} 円`;
        if (totalUsdt) totalUsdt.textContent = `参考:${totalProfitUsdt.toFixed(2)} USDT`;
      } else {
        if (dailyJpy) dailyJpy.textContent = '-';
        if (dailyUsdt) dailyUsdt.textContent = '-';
        if (monthlyJpy) monthlyJpy.textContent = '-';
        if (monthlyUsdt) monthlyUsdt.textContent = '-';
        if (totalJpy) totalJpy.textContent = '-';
        if (totalUsdt) totalUsdt.textContent = '-';
      }
    };

    principalInput?.addEventListener('input', calculate);
    planSelect?.addEventListener('change', calculate);

    calculate();

    return () => {
      principalInput?.removeEventListener('input', calculate);
      planSelect?.removeEventListener('change', calculate);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-2 sm:gap-3">
              <img 
                alt="BitradeX" 
                className="h-6 w-6 sm:h-7 sm:w-7 object-contain flex-shrink-0" 
                src="https://static.readdy.ai/image/c283ebba76f2231788b6e8a6bcedfde5/67bb25588a3178cfd4ccfa485ed4ebfb.jpeg"
              />
              <span className="text-sm sm:text-base font-semibold text-slate-800 leading-tight">
                <span className="hidden sm:inline">AI Crypto Support for BitradeX</span>
                <span className="sm:hidden">AI Crypto Support<br />for BitradeX</span>
              </span>
            </div>
            <nav className="hidden md:flex items-center space-x-6">
              <button 
                onClick={() => scrollToSection('about')}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                運営者について
              </button>
              <button 
                onClick={() => scrollToSection('video')}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                解説動画
              </button>
              <button 
                onClick={() => scrollToSection('simulator')}
                className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors whitespace-nowrap cursor-pointer"
              >
                収益シミュレーション
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section - ファーストビュー */}
      <section className="relative pt-28 sm:pt-36 pb-20 sm:pb-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto">
          <div className="text-center space-y-8 sm:space-y-10">
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 leading-tight px-2">
              仮想通貨が分からなくても大丈夫<br />
              <span className="text-[#1e3a5f]">日本在住の日本人が、<span className="text-[#1e3a5f] font-extrabold">BitradeX</span>の<span className="text-[#1e3a5f] font-extrabold">AIボット</span>取引を<br className="sm:hidden" />1から<span className="text-[#1e3a5f] font-extrabold">日本語でサポート</span>します</span>
            </h1>
            
            <div className="max-w-2xl mx-auto space-y-4 sm:space-y-5 text-left bg-white rounded-2xl p-6 sm:p-8 shadow-md border border-slate-100">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl text-[#2d5a87]"></i>
                </div>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed"><span className="text-[#1e3a5f] font-semibold">BitradeX</span>は海外の取引所のため、<span className="text-[#1e3a5f] font-semibold">日本語サポート</span>が充実していません。そのため、本サイトから登録された方は私が日本語で、すべてをサポートいたします。</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl text-[#2d5a87]"></i>
                </div>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed"><span className="text-[#1e3a5f] font-semibold">BitradeX</span>はイギリスに本社、アメリカにてMSBライセンスと言う金融ライセンスを継続取得し、規制の遵守を証明して適法に業務を行っています。</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl text-[#2d5a87]"></i>
                </div>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">さらに、2018年FIFAワールドカップで優勝した現役サッカー選手がアンバサダーに就任して頂き信頼性を確立しています。</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl text-[#2d5a87]"></i>
                </div>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed">仮想通貨の「か」の字もわからない方はご安心ください。入金から出金まで、0の状態からすべてをサポートします。</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-checkbox-circle-fill text-lg sm:text-xl text-[#2d5a87]"></i>
                </div>
                <p className="text-sm sm:text-base text-slate-700 leading-relaxed"><span className="text-[#1e3a5f] font-semibold">BitradeX</span>の<span className="text-[#1e3a5f] font-semibold">AIボット</span>に不信を抱いている方もご安心ください。<span className="text-[#1e3a5f] font-semibold">BitradeX</span>は取引所です。4つの利益構造があります。現物・先物・<span className="text-[#1e3a5f] font-semibold">AIボット</span>・カード事業です。今回利用するのは<span className="text-[#1e3a5f] font-semibold">AIボット</span>。日々の現物・先物取引でどのくらい利益を出しているのか？会社としてどのくらい資金を持っているのかも証明できます。常にWinWinの関係性であること、信頼できる器であることを証明できます。</p>
              </div>
            </div>

            {/* CTA① - ファーストビュー */}
            <div className="pt-6 sm:pt-8">
              <p className="text-sm sm:text-base text-slate-600 mb-3">登録は無料・数分で完了します</p>
              <a 
                href="https://www.bitradex.com/en/account/register?inviteCode=85BXT4" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="inline-flex items-center justify-center w-full sm:w-auto px-10 sm:px-12 py-5 sm:py-6 rounded-full bg-[#1e3a5f] text-white text-base sm:text-lg font-bold hover:bg-[#2d4a6f] transition-all whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
              >
                無料登録はこちら
                <div className="w-5 h-5 flex items-center justify-center ml-2">
                  <i className="ri-arrow-right-line text-xl"></i>
                </div>
              </a>
              <p className="text-xs text-slate-500 mt-2">※公式登録ページへ移動します</p>
              <p className="text-sm text-slate-600 mt-4 leading-relaxed">
                無料登録後、操作方法や出金方法をLINEにて個別サポートしています。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section - 信頼ゾーン */}
      <section id="about" className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <div className="space-y-10 sm:space-y-12">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-800 text-center px-2 tracking-tight">運営者のスタンス・紹介</h2>
            
            {/* YouTubeチャンネルトップ画像 */}
            <div className="flex justify-center">
              <div className="w-full sm:w-4/5 lg:w-3/4">
                <img 
                  src="https://static.readdy.ai/image/c283ebba76f2231788b6e8a6bcedfde5/5c6731fe9ca8ed8cac3abd5f8f976e35.jpeg"
                  alt="YouTubeチャンネル"
                  className="w-full h-auto rounded-2xl shadow-md"
                />
              </div>
            </div>

            <div className="prose prose-base sm:prose-lg max-w-none space-y-5 sm:space-y-6 text-slate-700 leading-relaxed text-left px-2">
              <p className="text-base sm:text-lg">
                はじめまして。<br />
                【2億円】仮想通貨に全ツッパした男の復活記録というyoutubeの運営者です。
              </p>
              
              <p className="text-base sm:text-lg">
                本サイトは、仮想通貨取引所「<span className="text-[#1e3a5f] font-semibold">BitradeX</span>」のAI Bot取引について、<br />
                日本人向けに情報を整理し、サポートを行う個人運営のサイトです。
              </p>
              
              <p className="text-base sm:text-lg">
                私は<span className="text-[#1e3a5f] font-semibold">BitradeX</span>の運営関係者ではなく、実際に利用している一般ユーザーです。
              </p>
              
              <p className="text-base sm:text-lg">
                最初は私自身も、<br />
                「本当に大丈夫なのか」<br />
                「仕組みがよく分からない」<br />
                と感じ、情報を集め、説明会やセミナーにも参加した上で判断しました。
              </p>
              
              <p className="text-base sm:text-lg">
                海外取引所は、日本語情報が少なく、<br />
                不安を感じたまま進めてしまうケースも少なくありません。
              </p>
              
              <p className="text-base sm:text-lg">
                そこで、<br />
                日本在住の日本人として、<br />
                操作や考え方を日本語で整理しながら進められる環境を作るため、<br />
                個人で情報整理とサポートを行っています。
              </p>
              
              <p className="text-base sm:text-lg">
                誇張や過度なおすすめは行わず、<br />
                中立的な立場で情報をまとめています。
              </p>
              
              <p className="text-base sm:text-lg font-bold text-slate-800">
                運営者プロフィール
              </p>
              
              <p className="text-base sm:text-lg">
                日本在住の個人ユーザー。<span className="text-[#1e3a5f] font-semibold">BitradeX</span>を実際に利用しながら、<br />
                同じように不安を感じている日本人の方に向けて情報整理とサポートを提供しています。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section - 解説動画 */}
      <section id="video" className="py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8 sm:space-y-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-800 text-center px-2 tracking-tight"><span className="text-[#1e3a5f] font-extrabold">BitradeX</span>を実際に調べて分かったこと</h2>
            
            <div className="bg-amber-50 border-l-4 border-amber-500 rounded-lg p-5 sm:p-6">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-information-line text-lg sm:text-xl text-amber-600"></i>
                </div>
                <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-slate-700 leading-relaxed">
                  <p>
                    登録を勧めるための動画ではありません。<br />
                    「自分に合うかどうか」を判断するための材料としてご覧ください。
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
              <div className="relative w-full" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/LHli9OyRqoE"
                  title="BitradeX解説動画"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Simulator Section - 収益シミュレーション */}
      <section id="simulator" className="py-20 sm:py-24 pb-10 sm:pb-12 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-4xl mx-auto">
          <div className="space-y-8 sm:space-y-10">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-800 text-center px-2 tracking-tight">収益シミュレーション（目安）</h2>
            
            <div className="bg-[#e8f0f7] border-l-4 border-[#2d5a87] rounded-lg p-5 sm:p-6">
              <div className="flex items-start space-x-3">
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-1">
                  <i className="ri-information-line text-lg sm:text-xl text-[#2d5a87]"></i>
                </div>
                <div className="space-y-2 sm:space-y-3 text-sm sm:text-base text-slate-700 leading-relaxed">
                  <p>
                    「元本を意識した進め方」「利益を重視した進め方」など、<br />
                    目的に応じた最適な運用方法を、私の経験をもとに伝えられます。<br />
                    登録後、LINEでご相談ください。
                  </p>
                </div>
              </div>
            </div>

            {/* Custom Calculator */}
            <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-slate-200 p-5 sm:p-6">
              <div className="flex gap-3 items-start justify-between flex-wrap mb-4">
                <div style={{ minWidth: '260px' }}>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-800 mb-2 tracking-tight">収益シミュレーション（目安）</h3>
                  <p className="text-xs sm:text-sm text-slate-700 leading-relaxed">
                    日本円に交換可能な金額
                  </p>
                </div>
              </div>

              <div className="p-4 border border-slate-200 rounded-xl bg-slate-50 mb-4">
                <div className="flex gap-3 flex-wrap items-center justify-between">
                  <div className="text-slate-600 text-xs mb-1">換算レート（固定）</div>
                  <div className="text-slate-700 text-sm">1 USDT ＝ <span id="cs_fx_label" className="font-black">155</span> 円</div>
                </div>
                <div className="mt-2 text-slate-600 text-xs leading-relaxed">
                  ※ 円表記で計算します。USDT換算は参考表示です（固定レート）。
                </div>
              </div>

              <div className="space-y-4">
                <label className="block">
                  <span className="text-sm sm:text-base font-bold text-slate-800 mb-2 block">① 元本（円）</span>
                  <input 
                    id="cs_principal_jpy" 
                    type="number" 
                    min="0" 
                    step="1" 
                    placeholder="例：150000"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm sm:text-base outline-none focus:border-[#2d5a87] focus:ring-2 focus:ring-[#2d5a87]/20"
                  />
                  <span id="cs_principal_usdt_hint" className="text-xs text-slate-600 mt-1 block">参考：- USDT</span>
                </label>

                <label className="block">
                  <span className="text-sm sm:text-base font-bold text-slate-800 mb-2 block">② 運用プラン（選択）</span>
                  <select 
                    id="cs_plan"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl text-sm sm:text-base bg-white outline-none focus:border-[#2d5a87] focus:ring-2 focus:ring-[#2d5a87]/20 cursor-pointer"
                  >
                    <option value="lock360">360日 元本ロック（想定日利 0.5%）</option>
                    <option value="lock180">180日 元本ロック（想定日利 0.4%）</option>
                    <option value="lock90">90日 元本ロック（想定日利 0.35%）</option>
                    <option value="lock30">30日 元本ロック（想定日利 0.3%）</option>
                    <option value="aidaily">AI Daily（即日出金可 / 想定 0.1%〜0.25%）※平均 0.175%で計算</option>
                  </select>
                </label>

                <div className="grid grid-cols-2 gap-3 sm:gap-4">
                  <div className="p-3 sm:p-4 border border-slate-200 rounded-xl">
                    <div className="text-slate-600 text-xs mb-1">③ 運用期間</div>
                    <div id="cs_days" className="text-base sm:text-lg font-black text-slate-800">360日</div>
                  </div>
                  <div className="p-3 sm:p-4 border border-slate-200 rounded-xl">
                    <div className="text-slate-600 text-xs mb-1">使用する想定日利</div>
                    <div id="cs_rate" className="text-base sm:text-lg font-black text-slate-800">0.5%</div>
                  </div>
                </div>

                <div className="space-y-3 mt-2">
                  <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                    <div className="text-slate-600 text-xs mb-1">④ 日利（引き出し可能分の目安）</div>
                    <div id="cs_daily_jpy" className="text-xl sm:text-2xl font-black text-slate-800 mt-1">-</div>
                    <div id="cs_daily_usdt" className="text-slate-600 text-xs mt-2">-</div>
                  </div>

                  <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                    <div className="text-slate-600 text-xs mb-1">⑤ 月収（30日換算 / 引き出し可能分の目安）</div>
                    <div id="cs_monthly_jpy" className="text-xl sm:text-2xl font-black text-slate-800 mt-1">-</div>
                    <div id="cs_monthly_usdt" className="text-slate-600 text-xs mt-2">-</div>
                  </div>

                  <div className="p-4 border border-slate-200 rounded-xl bg-slate-50">
                    <div className="text-slate-600 text-xs mb-1">⑥ 運用期間終了後の総利益（目安）</div>
                    <div id="cs_total_jpy" className="text-xl sm:text-2xl font-black text-slate-800 mt-1">-</div>
                    <div id="cs_total_usdt" className="text-slate-600 text-xs mt-2">-</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Support CTA Section - CVゾーン */}
      <section className="pt-10 sm:pt-12 pb-20 sm:pb-24 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-slate-100">
            <div className="p-6 sm:p-8 lg:p-12 space-y-6 sm:space-y-8">
              <div className="text-center space-y-4">
                <div className="w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center mx-auto bg-[#e8f0f7] rounded-full">
                  <i className="ri-customer-service-2-line text-2xl sm:text-3xl text-[#2d5a87]"></i>
                </div>
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-extrabold text-slate-800 px-2 tracking-tight">
                  登録・サポート導線
                </h2>
              </div>

              <div className="space-y-5 sm:space-y-6">
                {/* CTA③ - メイン */}
                <div className="text-center">
                  <p className="text-sm sm:text-base text-slate-600 mb-3">登録は無料・数分で完了します</p>
                  <a 
                    href="https://www.bitradex.com/en/account/register?inviteCode=85BXT4" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center justify-center w-full sm:w-auto px-8 sm:px-10 py-4 sm:py-5 rounded-full bg-[#1e3a5f] text-white text-base sm:text-lg font-bold hover:bg-[#2d4a6f] transition-colors whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl"
                  >
                    無料登録はこちら
                    <div className="w-5 h-5 flex items-center justify-center ml-2">
                      <i className="ri-arrow-right-line text-xl"></i>
                    </div>
                  </a>
                  <p className="text-xs text-slate-500 mt-2">※公式登録ページへ移動します</p>
                  <p className="text-sm text-slate-600 mt-4 leading-relaxed">
                    無料登録後、操作方法や出金方法をLINEにて個別サポートしています。
                  </p>
                </div>

                {/* CTA③ - 補助 */}
                <div className="text-center pt-4">
                  <a 
                    href="https://lin.ee/liB3jhC" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="inline-flex items-center justify-center w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 rounded-full bg-[#06c755] text-white text-sm sm:text-base font-bold hover:bg-[#05b34c] transition-colors cursor-pointer shadow-md hover:shadow-lg whitespace-nowrap"
                  >
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center mr-2">
                      <i className="ri-line-fill text-xl sm:text-2xl"></i>
                    </div>
                    LINE相談はこちら
                  </a>
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>

      {/* BitradeX関連情報セクション */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-3xl mx-auto">
          <h3 className="text-lg sm:text-xl font-extrabold text-slate-800 mb-6 tracking-tight">
            ▼ BitradeX関連情報
          </h3>
          <ul className="space-y-3">
            <li>
              <a 
                href="/bitradex-withdrawal" 
                className="text-base sm:text-lg text-[#1e3a5f] hover:text-[#2d5a87] transition-colors cursor-pointer flex items-center"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </div>
                出金方法
              </a>
            </li>
            <li>
              <a 
                href="/bitradex-deposit" 
                className="text-base sm:text-lg text-[#1e3a5f] hover:text-[#2d5a87] transition-colors cursor-pointer flex items-center"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </div>
                入金方法
              </a>
            </li>
            <li>
              <a 
                href="/bitradex-safety" 
                className="text-base sm:text-lg text-[#1e3a5f] hover:text-[#2d5a87] transition-colors cursor-pointer flex items-center"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </div>
                安全性について
              </a>
            </li>
            <li>
              <a 
                href="/bitradex-seminar-review" 
                className="text-base sm:text-lg text-[#1e3a5f] hover:text-[#2d5a87] transition-colors cursor-pointer flex items-center"
              >
                <div className="w-5 h-5 flex items-center justify-center mr-2">
                  <i className="ri-arrow-right-s-line text-lg"></i>
                </div>
                セミナー潜入調査
              </a>
            </li>
          </ul>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1e3a5f] text-white py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="space-y-4 sm:space-y-6">
              <div className="flex items-center space-x-2">
                <img 
                  alt="BitradeX" 
                  className="h-8 w-8 object-contain" 
                  src="https://static.readdy.ai/image/c283ebba76f2231788b6e8a6bcedfde5/67bb25588a3178cfd4ccfa485ed4ebfb.jpeg"
                />
                <h3 className="text-lg sm:text-xl font-bold tracking-tight">AI Crypto Support for <span className="font-extrabold">BitradeX</span></h3>
              </div>
              <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                <span className="font-semibold text-white">BitradeX</span>の<span className="font-semibold text-white">AIボット</span>取引について、日本人向けに情報を整理し、<br />
                サポートを行う個人運営のサイトです。
              </p>
            </div>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <h4 className="text-xs sm:text-sm font-bold uppercase mb-3">BitradeX関連情報</h4>
                <ul className="space-y-2">
                  <li>
                    <a href="/bitradex-withdrawal" className="text-xs sm:text-sm text-slate-300 hover:text-white transition-colors cursor-pointer">
                      出金方法
                    </a>
                  </li>
                  <li>
                    <a href="/bitradex-deposit" className="text-xs sm:text-sm text-slate-300 hover:text-white transition-colors cursor-pointer">
                      入金方法
                    </a>
                  </li>
                  <li>
                    <a href="/bitradex-safety" className="text-xs sm:text-sm text-slate-300 hover:text-white transition-colors cursor-pointer">
                      安全性について
                    </a>
                  </li>
                  <li>
                    <a href="/bitradex-seminar-review" className="text-xs sm:text-sm text-slate-300 hover:text-white transition-colors cursor-pointer">
                      セミナー潜入調査
                    </a>
                  </li>
                </ul>
              </div>
            </div>
            <div className="space-y-4 sm:space-y-6">
              <div>
                <h4 className="text-xs sm:text-sm font-bold uppercase mb-3">免責事項</h4>
                <p className="text-xs text-slate-300 leading-relaxed">
                  本サイトの情報は参考情報であり、投資判断は自己責任で行ってください。<br />
                  損失について当サイトは一切の責任を負いません。<br />
                  本サイトのサポートは、投資助言や利益保証を行うものではありません。
                </p>
              </div>
              <div>
                <h4 className="text-xs sm:text-sm font-bold uppercase mb-3">お問い合わせ</h4>
                <a 
                  href="https://lin.ee/liB3jhC" 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="inline-flex items-center text-xs sm:text-sm text-slate-300 hover:text-white transition-colors cursor-pointer"
                >
                  <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center mr-2">
                    <i className="ri-line-fill text-base sm:text-lg"></i>
                  </div>
                  LINEでお問い合わせ
                </a>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
            <p className="text-xs sm:text-sm text-slate-300">© 2025 AI Crypto Support for BitradeX. All rights reserved.</p>
            <div className="flex items-center space-x-6 text-xs sm:text-sm text-slate-300">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors cursor-pointer whitespace-nowrap">Powered by Readdy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
